import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  isSameMonth,
  isSameDay,
} from 'date-fns';

export function ScheduleCalendar({ meetings, onMeetingClick }) {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);

  const meetingsByDate = useMemo(() => {
    const map = {};
    meetings.forEach((m) => {
      if (!m.date) return;
      (map[m.date] = map[m.date] || []).push(m);
    });
    return map;
  }, [meetings]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    const list = [];
    for (let d = start; d <= end; d = addDays(d, 1)) list.push(d);
    return list;
  }, [month]);

  const today = new Date();
  const selectedKey = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
  const selectedMeetings = selectedKey ? meetingsByDate[selectedKey] || [] : [];

  return (
    <div className="space-y-4" data-testid="schedule-calendar">
      <div className="bg-surface border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif font-bold text-lg text-foreground" data-testid="calendar-month-label">
            {format(month, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMonth((m) => addMonths(m, -1))}
              className="p-2 rounded-lg hover:bg-surface-raised text-text-secondary hover:text-foreground transition-colors"
              data-testid="calendar-prev-month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setMonth(startOfMonth(new Date())); setSelectedDay(new Date()); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-surface-raised text-text-secondary hover:text-foreground transition-colors"
              data-testid="calendar-today-btn"
            >
              Today
            </button>
            <button
              onClick={() => setMonth((m) => addMonths(m, 1))}
              className="p-2 rounded-lg hover:bg-surface-raised text-text-secondary hover:text-foreground transition-colors"
              data-testid="calendar-next-month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 mb-1">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
            <div key={d} className="text-center text-xs text-text-muted font-medium py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const key = format(day, 'yyyy-MM-dd');
            const dayMeetings = meetingsByDate[key] || [];
            const inMonth = isSameMonth(day, month);
            const isToday = isSameDay(day, today);
            const isSelected = selectedDay && isSameDay(day, selectedDay);
            return (
              <button
                key={key}
                onClick={() => setSelectedDay(day)}
                className={`min-h-[52px] sm:min-h-[64px] rounded-lg p-1 flex flex-col items-center gap-1 border transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-transparent hover:bg-surface-raised'
                } ${inMonth ? '' : 'opacity-35'}`}
                data-testid={`calendar-day-${key}`}
              >
                <span
                  className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-primary text-background font-semibold' : 'text-foreground'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {dayMeetings.length > 0 && (
                  <span className="flex gap-0.5 flex-wrap justify-center">
                    {dayMeetings.slice(0, 3).map((m) => (
                      <span key={m.id} className="w-1.5 h-1.5 rounded-full bg-primary" />
                    ))}
                    {dayMeetings.length > 3 && (
                      <span className="text-[9px] text-primary leading-none">+{dayMeetings.length - 3}</span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="space-y-2" data-testid="calendar-day-details">
          <h3 className="text-sm font-medium text-text-secondary">
            {format(selectedDay, 'EEEE, MMMM d')} — {selectedMeetings.length || 'no'} meeting
            {selectedMeetings.length === 1 ? '' : 's'}
          </h3>
          {selectedMeetings.map((meeting) => (
            <button
              key={meeting.id}
              onClick={() => onMeetingClick(meeting)}
              className="w-full bg-surface border border-border hover:border-primary/50 rounded-xl p-3 text-left transition-all"
              data-testid={`calendar-meeting-${meeting.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-foreground text-sm">{meeting.title}</div>
                  <div className="text-xs text-text-muted mt-0.5">
                    {meeting.client_name} {meeting.time && `· ${meeting.time}`}
                  </div>
                </div>
                {meeting.booked_by_client && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/20">
                    self-booked
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
